import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Loader2, QrCode, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { insertBlock, emit } from "./cmd";
import { esc } from "../../lib/pages";
import { post, get, webOrigin, PAYPAL_ME, paypalMeUrl } from "../../lib/pro";
import { money } from "../../pages/PayInvoice";

const block = (l, qr) => `<table class="pay-block" data-paylink="${esc(l.link_id)}"><tbody><tr><td style="border:none;width:130px;vertical-align:top"><img src="${qr}" alt="Pay ${esc(l.reference)}" style="width:120px;height:120px"></td><td style="border:none;vertical-align:top"><b>Pay online · S$${money(l.amount)}</b>\nReference: ${esc(l.reference)}\nScan the QR code with your phone camera or banking app. Pay by PayNow (DBS, OCBC, UOB…) or Visa / Mastercard.\n<a href="${esc(l.url)}" target="_blank" rel="noopener noreferrer">${esc(l.url)}</a>\nStatus: <span class="pay-status">Awaiting payment</span></td></tr></tbody></table><p><br></p>`;

export const PayLinkDialog = ({ open, onClose, rootRef, title }) => {
  const [amount, setAmount] = useState("");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState("");
  const [links, setLinks] = useState([]);
  const [foreign, setForeign] = useState(false);

  useEffect(() => {
    if (!open) return;
    const root = rootRef.current;
    const total = [...root.querySelectorAll("td")].reverse().find((td) => /total/i.test(td.textContent) && td.nextElementSibling)?.nextElementSibling?.textContent;
    setAmount(total ? total.replace(/[^0-9.]/g, "") : "");
    setForeign(!!total && /[£€$¥₹]|USD|GBP|EUR/i.test(total) && !/S\$|SGD/i.test(total));
    setRef(root.innerText.match(/INV-\d+/)?.[0] || title || "Invoice");
    setLinks([...root.querySelectorAll("[data-paylink]")].map((t) => t.dataset.paylink));
  }, [open, rootRef, title]);

  const create = async (e) => {
    e.preventDefault();
    if (!navigator.onLine) return toast.error("You are offline. Payment links need an internet connection.");
    setBusy("create");
    try {
      const l = await post("/api/payments/invoice/link", { amount: Number(amount), currency: "sgd", reference: ref, origin_url: webOrigin() });
      const qr = await QRCode.toDataURL(l.url, { margin: 1, width: 240 });
      onClose();
      setTimeout(() => { insertBlock(block(l, qr)); toast.success("Payment QR code added to the invoice"); }, 0);
    } catch (x) { toast.error(x.message); }
    setBusy("");
  };

  const insertPaypal = async () => {
    const url = paypalMeUrl(amount);
    const qr = await QRCode.toDataURL(url, { margin: 1, width: 280, color: { dark: "#003087" } });
    const amt = Number(amount) >= 1 ? ` · S$${money(amount)}` : "";
    onClose();
    setTimeout(() => {
      insertBlock(`<table class="pay-block paypal-static"><tbody><tr><td style="border:none;width:150px;vertical-align:top"><img src="${qr}" alt="PayPal QR code" style="width:140px;height:140px"></td><td style="border:none;vertical-align:top"><b>Pay by PayPal${amt}</b>\nReference: ${esc(ref || "Invoice")}\nScan with your phone camera or PayPal app, and add the invoice reference in the note.\n<a href="${esc(url)}" target="_blank" rel="noopener noreferrer">${esc(url)}</a></td></tr></tbody></table><p><br></p>`);
      toast.success("PayPal QR code added");
    }, 0);
  };

  const insertStatic = async () => {
    const blob = await (await fetch("/paynow-qr.jpeg")).blob();
    const qr = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob); });
    const amt = Number(amount) >= 1 ? ` · S$${money(amount)}` : "";
    onClose();
    setTimeout(() => {
      insertBlock(`<table class="pay-block paynow-static"><tbody><tr><td style="border:none;width:150px;vertical-align:top"><img src="${qr}" alt="PayNow QR code" style="width:140px;height:140px"></td><td style="border:none;vertical-align:top"><b>Pay by PayNow${amt}</b>\nReference: ${esc(ref || "Invoice")}\nScan with your banking app (DBS, POSB, OCBC, UOB…) and enter the invoice reference.</td></tr></tbody></table><p><br></p>`);
      toast.success("PayNow QR code added");
    }, 0);
  };

  const check = async (id) => {
    setBusy(id);
    try {
      const s = await get(`/api/payments/invoice/link/${id}`);
      const el = rootRef.current.querySelector(`[data-paylink="${id}"] .pay-status`);
      if (s.paid) {
        if (el) el.textContent = `Paid ${new Date(s.paid_at).toLocaleDateString("en-SG")}`;
        rootRef.current.querySelectorAll("select.dd-field").forEach((sel) => {
          const o = [...sel.options].find((x) => x.text === "Paid");
          if (o) { sel.value = o.value; [...sel.options].forEach((x) => x.toggleAttribute("selected", x === o)); }
        });
        emit();
        toast.success(`${s.reference}: paid S$${money(s.amount)}`);
      } else toast(`${s.reference}: not paid yet`);
    } catch (x) { toast.error(x.message); }
    setBusy("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] rounded-lg sm:max-w-md" data-testid="paylink-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-primary" />Request payment</DialogTitle>
          <DialogDescription>“Add payment QR” creates a card + PayNow link that confirms payment automatically (paid to your Stripe account). “Insert my PayNow / PayPal QR” adds your own QR — mark the invoice Paid yourself.</DialogDescription>
        </DialogHeader>
        <form onSubmit={create} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Amount (SGD)</Label><Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="paylink-amount-input" /></div>
            <div className="space-y-1"><Label>Reference</Label><Input value={ref} maxLength={60} onChange={(e) => setRef(e.target.value)} data-testid="paylink-ref-input" /></div>
          </div>
          {foreign && <p className="text-xs text-amber-700 bg-amber-500/10 rounded-md p-2" data-testid="paylink-currency-warning">The invoice total is not in Singapore dollars. Payments are collected in SGD — enter the SGD amount.</p>}
          <DialogFooter className="gap-2 sm:gap-2 flex-wrap"><Button type="button" variant="outline" onClick={insertStatic} data-testid="paylink-static-paynow-btn"><QrCode className="h-4 w-4 mr-1.5" />Insert my PayNow QR</Button>
            {PAYPAL_ME && <Button type="button" variant="outline" className="text-[#003087]" onClick={insertPaypal} data-testid="paylink-static-paypal-btn"><QrCode className="h-4 w-4 mr-1.5" />Insert my PayPal QR</Button>}<Button type="submit" disabled={!(Number(amount) >= 1) || !ref.trim() || !!busy} data-testid="paylink-create-btn">{busy === "create" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add payment QR</Button></DialogFooter>
        </form>
        {links.length > 0 && (
          <div className="border-t pt-3 space-y-2" data-testid="paylink-existing">
            <div className="text-xs font-medium text-muted-foreground">Payment links in this document</div>
            {links.map((id) => (
              <div key={id} className="flex items-center justify-between gap-2 text-xs">
                <span className="font-mono truncate">{id}</span>
                <Button size="sm" variant="outline" onClick={() => check(id)} disabled={!!busy} data-testid={`paylink-check-${id}`}>{busy === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}Check paid</Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
